import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { KpiRow, KpiStrip, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { MedioRediviaCobro, RediviaCobro, RediviaNegocio } from '@/lib/types'
import {
  IconAlertTriangle, IconBan, IconCreditCard, IconFilePlus, IconPlus, IconRefresh,
  IconSearch, IconWallet,
} from '@/components/icons/Icons'

const ESTADO_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  cobrado: 'Cobrado',
  anulado: 'Anulado',
}

const ESTADO_BADGE: Record<string, string> = {
  pendiente: 'badge-warning',
  cobrado: 'badge-aceptada',
  anulado: 'badge-rechazada',
}

const MEDIOS: MedioRediviaCobro[] = ['Transferencia', 'MercadoPago', 'Efectivo']

/** El mes en curso, como lo espera el backend. */
const periodoActual = () => new Date().toISOString().slice(0, 7)

export default function RediviaCobros() {
  const api = useApi()

  const [cobros, setCobros] = useState<RediviaCobro[]>([])
  const [negocios, setNegocios] = useState<RediviaNegocio[]>([])
  const [loading, setLoading] = useState(true)
  const [ocupado, setOcupado] = useState<number | null>(null)

  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroPeriodo, setFiltroPeriodo] = useState('todos')

  const [modalGenerar, setModalGenerar] = useState(false)
  const [genNegocio, setGenNegocio] = useState<number | ''>('')
  const [genPeriodo, setGenPeriodo] = useState(periodoActual())

  const [modalPago, setModalPago] = useState<RediviaCobro | null>(null)
  const [medio, setMedio] = useState<MedioRediviaCobro>('Transferencia')
  const [referencia, setReferencia] = useState('')

  const cargar = useCallback(async () => {
    try {
      const [c, n] = await Promise.all([
        api.get<{ cobros: RediviaCobro[] }>('/api/redivia/cobros'),
        api.get<{ negocios: RediviaNegocio[] }>('/api/redivia/negocios'),
      ])
      setCobros(c.cobros || [])
      setNegocios(n.negocios || [])
    } catch (e: any) {
      showToast(e?.message || 'No se pudieron cargar los cobros', 'error')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { void cargar() }, [cargar])

  const periodos = useMemo(
    () => [...new Set(cobros.map(c => c.periodo))].sort((a, b) => b.localeCompare(a)),
    [cobros],
  )

  const filtrados = useMemo(() => {
    let r = cobros
    if (filtroEstado !== 'todos') r = r.filter(c => c.estado === filtroEstado)
    if (filtroPeriodo !== 'todos') r = r.filter(c => c.periodo === filtroPeriodo)
    if (search) {
      const q = search.toLowerCase()
      r = r.filter(c =>
        c.negocio_nombre?.toLowerCase().includes(q) ||
        c.tenant_slug?.toLowerCase().includes(q) ||
        c.factura_numero?.toLowerCase().includes(q) ||
        c.referencia?.toLowerCase().includes(q)
      )
    }
    return r
  }, [cobros, filtroEstado, filtroPeriodo, search])

  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtrados, 15, `${search}|${filtroEstado}|${filtroPeriodo}`)

  const hayFiltros = !!search || filtroEstado !== 'todos' || filtroPeriodo !== 'todos'

  /* Lo que importa de la cobranza no es cuántos cobros hay, sino cuánto falta
     emitir, cuánto falta que entre, y qué pago no llegó al panel del cliente. */
  const resumen = useMemo(() => {
    const porCobrar: Record<string, number> = {}
    for (const c of cobros) {
      if (c.estado !== 'pendiente') continue
      porCobrar[c.moneda] = Math.round(((porCobrar[c.moneda] ?? 0) + c.monto) * 100) / 100
    }
    return {
      sinComprobante: cobros.filter(c => c.estado === 'pendiente' && !c.factura_id).length,
      porCobrar,
      avisosFallidos: cobros.filter(c => !!c.aviso_error).length,
    }
  }, [cobros])

  const accion = async (cobro: RediviaCobro, ruta: string, cuerpo: unknown, exito: string) => {
    setOcupado(cobro.id)
    try {
      const r = await api.post<any>(`/api/redivia/cobros/${cobro.id}/${ruta}`, cuerpo)
      showToast(exito, 'success')
      if (r?.aviso_sin_correo) showToast(r.aviso_sin_correo, 'error')
      if (r?.aviso && r.aviso.ok === false) {
        showToast(`El pago quedó registrado, pero Redivia no se enteró: ${r.aviso.error}`, 'error')
      }
      await cargar()
      return r
    } catch (e: any) {
      showToast(e?.message || 'No se pudo completar la acción', 'error')
      return null
    } finally {
      setOcupado(null)
    }
  }

  const generar = async () => {
    if (!genNegocio) { showToast('Elegí el negocio', 'error'); return }
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(genPeriodo)) { showToast('El período va como AAAA-MM', 'error'); return }
    try {
      const r = await api.post<{ ya_existia: boolean }>(`/api/redivia/negocios/${genNegocio}/cobros`, { periodo: genPeriodo })
      showToast(r.ya_existia ? 'Ese período ya tenía cobro' : 'Cobro generado', r.ya_existia ? 'info' : 'success')
      setModalGenerar(false)
      await cargar()
    } catch (e: any) {
      showToast(e?.message || 'No se pudo generar el cobro', 'error')
    }
  }

  const linkPago = async (cobro: RediviaCobro) => {
    const r = await accion(cobro, 'link-pago', {}, 'Link de pago listo')
    if (r?.url) {
      await navigator.clipboard.writeText(r.url).catch(() => {})
      showToast('Link copiado al portapapeles', 'info')
    }
  }

  const registrarPago = async () => {
    if (!modalPago) return
    const ok = await accion(modalPago, 'pagado', { medio, referencia: referencia || null }, 'Pago registrado')
    if (ok) { setModalPago(null); setReferencia('') }
  }

  if (loading) return (
    <div className="redivia__cargando">
      <div className="auth-spinner" />Cargando cobros...
    </div>
  )

  return (
    <>
      <div className="page-actions">
        <button className="btn btn-primary" onClick={() => { setGenPeriodo(periodoActual()); setModalGenerar(true) }}
          disabled={negocios.length === 0}>
          <IconPlus size={16} /> Generar cobro
        </button>
      </div>

      {cobros.length > 0 && (
        <KpiStrip>
          {Object.entries(resumen.porCobrar).map(([moneda, monto]) => (
            <KpiRow key={moneda} moneda={moneda}>
              <StatCard label="Por cobrar" value={formatCurrency(monto, moneda)} tone="warning" />
            </KpiRow>
          ))}
          <KpiRow>
            <StatCard label="Sin comprobante" value={resumen.sinComprobante}
              tone={resumen.sinComprobante > 0 ? 'warning' : 'default'} />
            <StatCard label="Avisos fallidos" value={resumen.avisosFallidos}
              tone={resumen.avisosFallidos > 0 ? 'danger' : 'default'} />
          </KpiRow>
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por empresa, tenant, comprobante u operación..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 170 }}>
          <option value="todos">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {periodos.length > 1 && (
          <select className="form-input" value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)} style={{ width: 130 }}>
            <option value="todos">Período</option>
            {periodos.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear"
            onClick={() => { setSearch(''); setFiltroEstado('todos'); setFiltroPeriodo('todos') }}>Limpiar filtros</button>
        )}
      </div>

      {cobros.length === 0 ? (
        <div className="empty-state">
          <h3>Sin cobros</h3>
          <p>Generá el cobro del mes de un negocio, o esperá a que el cron lo haga el día de cobro.</p>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Negocio</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Comprobante</th>
                  <th>Pago</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginated.map(c => (
                  <tr key={c.id} className={c.estado === 'anulado' ? 'row--dimmed' : undefined}>
                    <td>{c.periodo}</td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{c.negocio_nombre || '—'}</span>
                        {c.tenant_slug && <span className="cell-stack__sub">{c.tenant_slug}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{formatCurrency(c.monto, c.moneda)}</span>
                        {c.igv != null && (
                          <span className="cell-stack__sub">
                            base {formatCurrency(c.base_imponible ?? 0, c.moneda)} + IGV {formatCurrency(c.igv, c.moneda)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td><span className={`badge ${ESTADO_BADGE[c.estado] || ''}`}>{ESTADO_LABEL[c.estado] || c.estado}</span></td>
                    <td>
                      {c.factura_serie_numero
                        ? <div className="cell-stack">
                            <span className="cell-stack__title">{c.factura_serie_numero}</span>
                            <span className="cell-stack__sub">{c.factura_numero}</span>
                          </div>
                        : c.factura_numero
                          ? <span>{c.factura_numero}</span>
                          : <span className="cell-soft">sin emitir</span>}
                    </td>
                    <td>
                      {c.estado === 'cobrado'
                        ? <div className="cell-stack">
                            <span className="cell-stack__title">{c.medio || '—'}</span>
                            <span className="cell-stack__sub">
                              {c.referencia ? `op. ${c.referencia}` : 'sin nº de operación'}
                              {c.pagado_at ? ` · ${formatDate(c.pagado_at)}` : ''}
                            </span>
                          </div>
                        : <span className="cell-soft">—</span>}
                    </td>
                    <td className="row-actions row-actions--nowrap">
                      {/* Un pago que no llegó al panel deja al cliente pagando
                          con el acceso cerrado: tiene que verse y reintentarse. */}
                      {c.aviso_error && (
                        <button className="btn btn-sm btn-secondary" onClick={() => void accion(c, 'reintentar-aviso', {}, 'Aviso reenviado')}
                          disabled={ocupado === c.id} title={`Redivia no se enteró del pago: ${c.aviso_error}`}>
                          <IconAlertTriangle size={14} />
                        </button>
                      )}
                      {c.estado === 'pendiente' && !c.factura_id && (
                        <button className="btn btn-sm btn-secondary" onClick={() => void accion(c, 'facturar', {}, 'Comprobante emitido')}
                          disabled={ocupado === c.id} title="Emitir el comprobante">
                          <IconFilePlus size={14} />
                        </button>
                      )}
                      {c.estado === 'pendiente' && c.factura_id && (
                        <>
                          <button className="btn btn-sm btn-secondary" onClick={() => void linkPago(c)}
                            disabled={ocupado === c.id} title="Crear y copiar el link de pago de Mercado Pago">
                            <IconCreditCard size={14} />
                          </button>
                          <button className="btn btn-sm btn-primary"
                            onClick={() => { setModalPago(c); setMedio('Transferencia'); setReferencia('') }}
                            disabled={ocupado === c.id} title="Registrar el pago">
                            <IconWallet size={14} />
                          </button>
                        </>
                      )}
                      {c.estado === 'pendiente' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => void accion(c, 'anular', {}, 'Cobro anulado')}
                          disabled={ocupado === c.id} title="Anular el cobro">
                          <IconBan size={14} />
                        </button>
                      )}
                      {c.estado === 'cobrado' && c.avisado_at && (
                        <button className="btn btn-sm btn-secondary" onClick={() => void accion(c, 'reintentar-aviso', {}, 'Aviso reenviado')}
                          disabled={ocupado === c.id} title={`Avisado a Redivia el ${formatDate(c.avisado_at)}. Reenviar.`}>
                          <IconRefresh size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} noun="cobros" />
        </>
      )}

      <Modal
        title="Generar cobro"
        isOpen={modalGenerar}
        onClose={() => setModalGenerar(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalGenerar(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={() => void generar()}>Generar</button>
          </>
        }
      >
        <div>
          <div className="form-group">
            <label className="form-label">Negocio</label>
            <select className="form-input" value={genNegocio}
              onChange={e => setGenNegocio(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Elegir negocio...</option>
              {negocios.filter(n => n.estado !== 'cancelado').map(n => (
                <option key={n.id} value={n.id}>
                  {n.cliente_nombre || n.tenant_slug} — {formatCurrency(n.monto_mensual, n.moneda)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Período</label>
            <input className="form-input" placeholder="2026-11" value={genPeriodo}
              onChange={e => setGenPeriodo(e.target.value.trim())} />
            <span className="form-hint">Un cobro por negocio y mes: repetir el período devuelve el que ya existe.</span>
          </div>
        </div>
      </Modal>

      <Modal
        title={modalPago ? `Registrar pago — ${modalPago.periodo}` : 'Registrar pago'}
        isOpen={modalPago !== null}
        onClose={() => setModalPago(null)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalPago(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={() => void registrarPago()} disabled={ocupado !== null}>
              {ocupado !== null ? 'Registrando...' : 'Registrar pago'}
            </button>
          </>
        }
      >
        <div>
          <div className="form-group">
            <label className="form-label">Medio</label>
            <select className="form-input" value={medio} onChange={e => setMedio(e.target.value as MedioRediviaCobro)}>
              {MEDIOS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Número de operación</label>
            <input className="form-input" placeholder="0012345678" value={referencia}
              onChange={e => setReferencia(e.target.value)} />
            <span className="form-hint">Sin esto el cobro no se puede cruzar con el extracto del banco.</span>
          </div>
          <div className="form-group">
            <span className="form-hint">
              Registrar el pago marca la factura pagada, reconoce el ingreso en caja (categoría Redivia)
              y le avisa a Redivia para que levante la suspensión del panel.
            </span>
          </div>
        </div>
      </Modal>
    </>
  )
}
